/**
 * Projekt dachu 3D: układ modułów całego projektu, przeszkody do cieni i produkcja.
 *
 * Łączy `bryla.js` (geometria), `panele.js` (układ na połaciach), `cienie.js` (promienie),
 * `slonce.js` i `uzysk.js` (słońce, uzysk kalibrowany do PVGIS). Czyste funkcje, bez Reacta i three.js.
 *
 * Moduły grupujemy po ustawieniu (nachylenie, azymut): połać skośna to jedna grupa, rzędy na dachu
 * płaskim w układzie wschód-zachód to dwie. Uzysk liczymy dla grupy, a stratę z cienia osobno dla
 * każdego modułu (6 próbek na moduł, jak sekcje diod bocznikujących).
 */
import { ukladNaPolaci, ukladNaDachuPlaskim, ukladGruntowy, ukladGruntowyWObszarze } from './panele'
import { uzyskMiesieczny, probkiRoku, strataCienia, przebiegDnia } from './uzysk'
import { zbudujPrzeszkody, ulamekCienia, trojkatyPudelka, trojkatyWielokata3d } from './cienie'
import { typElementu } from './projekt'
import { przeszkodyNaPolaci, brylaStojacej, brylaLukarny, wysokoscDachu, polacPod, PRZESZKODY_STOJACE } from './przeszkody'

// Wysokość dachu i połać pod punktem żyją w `przeszkody.js`; tu zostają pod dawnymi nazwami (widok, rysunek).
export { wysokoscDachu, polacPod }

const st = (r) => (r * 180) / Math.PI

/** Domyślnie moduły idą na połacie od wschodu przez południe do zachodu i na dachy płaskie. */
export const domyslnieAktywna = (polac) => polac.nachylenie < 5 || (polac.azymut >= 70 && polac.azymut <= 290 && (polac.pole ?? 10) >= 5)

/** Geometria komina w scenie: prostopadłościan od 0,6 m pod dachem do wysokości ponad dach. */
export const brylaKomina = (bryla, el) => brylaStojacej(bryla, { ...el, typ: 'komin' })

const kluczGrupy = (m) => {
  const nach = Math.round(st(Math.acos(Math.min(1, Math.max(-1, m.n[1])))) * 2) / 2
  let az = st(Math.atan2(m.n[0], -m.n[2]))
  if (az < 0) az += 360
  az = Math.round(az * 2) / 2
  return { klucz: `${nach}:${az}`, nachylenie: nach, azymut: az }
}

/** Kolejność miejsc w polu: od dolnego rzędu, w rzędzie od środka (zwarty, wyśrodkowany blok). */
const posortujMiejsca = (miejsca) => {
  const wRzedzie = new Map()
  for (const m of miejsca) wRzedzie.set(m.rzad, Math.max(wRzedzie.get(m.rzad) ?? 0, m.kol))
  return [...miejsca].sort((a, b) => (a.rzad - b.rzad)
    || (Math.abs(a.kol - wRzedzie.get(a.rzad) / 2) - Math.abs(b.kol - wRzedzie.get(b.rzad) / 2)) || (a.kol - b.kol))
}

/**
 * Obszar instalacji wolnostojącej zaznaczony na mapie: wielokąt [[x, z]...] w metrach sceny albo null
 * (projekty sprzed 2026-10-11 mają tylko blok `grunt.srodek`).
 */
export const obszarGruntu = (g) => {
  const w = g?.obszar
  return Array.isArray(w) && w.length >= 3 && w.every((p) => Array.isArray(p) && Number.isFinite(p[0]) && Number.isFinite(p[1])) ? w : null
}

/** Docelowa liczba modułów z trybu „liczba albo moc”. */
export const liczbaDocelowa = (moduly, mocModuluW) => {
  const c = moduly.cel || {}
  if (c.wedlug === 'moc') return Math.max(0, Math.round(((c.kwp || 0) * 1000) / Math.max(1, mocModuluW)))
  return Math.max(0, Math.round(c.liczba || 0))
}

/**
 * Czy strzałka może przesunąć moduły na połaci `id` (2026-10-30): żaden moduł nie znika, a dolna krawędź bloku idzie
 * w żądaną stronę. Blok oparty o krawędź dachu zawinąłby się na drugą stronę siatki i to właśnie tu odpada.
 */
export const ruchModulowMozliwy = (przed, po, id, wGore) => {
  const dol = (u) => {
    const naPolaci = u.moduly.filter((m) => m.polacId === id)
    return { liczba: naPolaci.length, dol: Math.min(...naPolaci.map((m) => m.srodek[0] * m.v[0] + m.srodek[1] * m.v[1] + m.srodek[2] * m.v[2])) }
  }
  const a = dol(przed)
  const b = dol(po)
  return b.liczba >= a.liczba && (wGore ? b.dol > a.dol : b.dol < a.dol)
}

/**
 * Układ modułów całego projektu.
 *
 * Każde „pole” (połać skośna, dach płaski, konstrukcja gruntowa) ma najpierw pełny układ miejsc, potem
 * wybór: tryb `max` bierze wszystkie miejsca; tryb `liczba` bierze docelową liczbę (sztuki albo moc),
 * rozdzieloną na pola od najlepszego uzysku na kWp, o ile użytkownik nie ustawił liczby na polu
 * (`naPolaci`). Na końcu ręczne poprawki: `usuniete` i `dodane` (identyfikatory miejsc).
 * Niewybrane miejsca trafiają do `wolne`, czyli do pól, które w edycji można kliknąć, żeby dodać moduł.
 *
 * Przeszkody (`przeszkody.js`) wycinają miejsca na połaciach skośnych i na dachu płaskim; lista wszystkich
 * połaci, także bez modułów, wraca w `przeszkody` (dla rysunku rzutu dachu).
 *
 * @returns { moduly, wolne, szyny, wsporniki, nogi, belki, grupy, przeszkody, pola: [{ id, nazwa, pojemnosc, liczba, uzyskNaKwp, nachylenie, azymut }] }
 */
export const ukladProjektu = ({ bryla, projekt, modul, lat = 52, lon = 19, zbieznosc = 0 }) => {
  const ust = projekt.moduly
  const usuniete = new Set(ust.usuniete || [])
  const dodane = new Set(ust.dodane || [])
  const wynik = { moduly: [], wolne: [], szyny: [], wsporniki: [], nogi: [], belki: [], pola: [], przeszkody: [] }
  const naDachu = (projekt.elementy || []).filter((e) => typElementu(e.typ)?.naDachu)
  const marg = ust.margines ?? 0.3
  const skrzydla = new Map((projekt.budynek.skrzydla || []).map((s) => [s.id, s]))
  const grunt = projekt.rodzaj === 'grunt'
  // Wolnostojąca z obszarem z mapy (wielokąt) ma oba tryby; stary blok bez obszaru tylko liczbę modułów.
  const obszar = grunt ? obszarGruntu(projekt.grunt) : null
  const tryb = grunt && !obszar ? 'liczba' : ust.tryb || 'max'
  const docelowa = liczbaDocelowa(ust, modul.moc)

  // Pola i ich układy: `uloz(pomin)` zwraca układ z pominięciem podanych miejsc.
  const pola = []
  if (obszar) {
    const g = projekt.grunt
    pola.push({
      id: 'grunt',
      nazwa: 'Obszar instalacji',
      uloz: (pomin) => ukladGruntowyWObszarze({
        obszar, obrot: g.obrot || 0, uklad: g.uklad === 'wschod_zachod' ? 'wschod_zachod' : 'poludnie', rzedy: Math.max(1, g.rzedy || 2),
        kat: g.kat ?? (g.uklad === 'wschod_zachod' ? 15 : 30), wysokoscDolna: g.wysokoscDolna ?? 0.8, modul, orientacja: g.orientacja || 'poziom',
        szerokoscGeograficzna: lat, usuniete: pomin, polacId: 'grunt',
        // Budynki obok (skrzydła) tylko cieniują: w ich rzucie stołów nie ma.
        wykluczenia: (bryla.obrysy || []).map((o) => o.punkty),
      }),
    })
  } else if (grunt) {
    const g = projekt.grunt || {}
    const wz = g.uklad === 'wschod_zachod'
    const rzedy = Math.max(1, g.rzedy || 2)
    const naStronie = wz ? 2 : 1
    const kolumny = Math.max(1, Math.ceil(Math.max(1, docelowa) / (rzedy * naStronie)))
    const kat = g.kat ?? (wz ? 15 : 30)
    const srodek = g.srodek || [0, 0]
    const obrot = g.obrot || 0
    const stol = (id, obrotStolu, przesun, pomin) => {
      const t = (obrotStolu * Math.PI) / 180
      const f = [Math.sin(t), Math.cos(t)]
      const czesc = ukladGruntowy({
        srodek: [srodek[0] + f[0] * przesun, srodek[1] + f[1] * przesun], obrot: obrotStolu, rzedy, kolumny, kat,
        wysokoscDolna: g.wysokoscDolna ?? 0.8, modul, orientacja: g.orientacja || 'poziom', y: 0,
        usuniete: new Set([...pomin].filter((x) => x.startsWith(`${id}:`)).map((x) => x.slice(id.length + 1))),
      })
      const zId = (m) => ({ ...m, id: `${id}:${m.id}`, polacId: id, pochylony: true })
      return { ...czesc, moduly: czesc.moduly.map(zId), wolne: czesc.wolne.map(zId) }
    }
    const wys = (g.orientacja || 'poziom') === 'poziom' ? modul.szerokosc : modul.dlugosc
    const glebokosc = (rzedy * wys + (rzedy - 1) * 0.02) * Math.cos((kat * Math.PI) / 180)
    if (wz) {
      // Dwa stoły plecami do siebie: wschodni i zachodni, kalenica w środku.
      pola.push({ id: 'grunt-w', nazwa: 'Konstrukcja, strona wschodnia', uloz: (pomin) => stol('grunt-w', obrot + 90, glebokosc / 2, pomin) })
      pola.push({ id: 'grunt-z', nazwa: 'Konstrukcja, strona zachodnia', uloz: (pomin) => stol('grunt-z', obrot - 90, glebokosc / 2, pomin) })
    } else {
      pola.push({ id: 'grunt', nazwa: 'Konstrukcja gruntowa', uloz: (pomin) => stol('grunt', obrot, 0, pomin) })
    }
  } else {
    for (const p of bryla.polacie || []) {
      // Przeszkody każdej połaci, także bez modułów (rysunek rzutu pokazuje okna i kominy na całym dachu).
      const przeszkody = przeszkodyNaPolaci({ bryla, elementy: naDachu, polac: p, margines: marg })
      wynik.przeszkody.push(...przeszkody)
      const ustP = ust.polacie?.[p.id] || {}
      const aktywna = ustP.aktywna ?? domyslnieAktywna(p)
      if (!aktywna) continue
      const orientacja = ustP.orientacja || ust.orientacja
      if (p.nachylenie < 5) {
        // `obszar` z bryły uwzględnia grubość attyki; bez niego prostokąt skrzydła.
        const s = skrzydla.get(p.skrzydloId)
        if (!s && !p.obszar) continue
        const obszar = p.obszar || { srodek: s.srodek, dlugosc: s.dlugosc, szerokosc: s.szerokosc, obrot: s.obrot }
        pola.push({
          id: p.id, polac: p, nazwa: 'Dach płaski',
          uloz: (pomin) => {
            const c = ukladNaDachuPlaskim({
              obszar, y: p.obszar?.y ?? Math.max(...p.wierzcholki.map((v) => v[1])),
              modul, orientacja: ustP.orientacja || 'poziom',
              kat: ust.plaski?.kat ?? 13, uklad: ust.plaski?.uklad || 'poludnie',
              szerokoscGeograficzna: lat, margines: Math.max(0.6, marg), przeszkody, usuniete: pomin,
            })
            // Rzędy na stelażach cieniują się nawzajem (patrz przeszkodyProjektu).
            const zP = (m) => ({ ...m, polacId: p.id, pochylony: true })
            return { ...c, moduly: (c.moduly || []).map(zP), wolne: (c.wolne || []).map(zP) }
          },
        })
      } else {
        pola.push({
          id: p.id, polac: p,
          uloz: (pomin) => ukladNaPolaci({
            polac: p, modul, orientacja, margines: { okap: marg, kalenica: marg, boki: marg }, przeszkody, usuniete: pomin, przesuniecieV: ustP.przesuniecie || 0,
          }),
        })
      }
    }
  }

  // Pełne układy i uzysk na kWp każdego pola (kolejność przydziału w trybie „liczba”).
  for (const pole of pola) {
    const pelny = pole.uloz(new Set())
    pole.miejsca = posortujMiejsca(pelny.moduly || [])
    const wzor = pole.miejsca[0]
    if (wzor) {
      const g = kluczGrupy(wzor)
      pole.nachylenie = g.nachylenie
      pole.azymut = g.azymut
      pole.uzyskNaKwp = uzyskMiesieczny({ lat, lon, nachylenie: g.nachylenie, azymut: azPrawdziwy(g.azymut, zbieznosc) }).rok
    } else {
      pole.uzyskNaKwp = 0
    }
  }

  // Liczba na każdym polu.
  const naPolu = new Map()
  if (tryb === 'max') {
    for (const pole of pola) naPolu.set(pole.id, pole.miejsca.length)
  } else {
    let zostalo = docelowa
    for (const pole of pola) {
      const reczna = ust.naPolaci?.[pole.id]
      if (reczna != null) {
        const n = Math.min(pole.miejsca.length, Math.max(0, reczna))
        naPolu.set(pole.id, n)
        zostalo -= n
      }
    }
    const pozostale = pola.filter((pole) => !naPolu.has(pole.id)).sort((a, b) => b.uzyskNaKwp - a.uzyskNaKwp)
    for (const pole of pozostale) {
      const n = Math.max(0, Math.min(pole.miejsca.length, zostalo))
      naPolu.set(pole.id, n)
      zostalo -= n
    }
  }

  for (const pole of pola) {
    const n = naPolu.get(pole.id) ?? 0
    const wybrane = new Set(pole.miejsca.slice(0, n).map((m) => m.id))
    for (const id of usuniete) wybrane.delete(id)
    for (const m of pole.miejsca) if (dodane.has(m.id)) wybrane.add(m.id)
    const pomin = new Set(pole.miejsca.filter((m) => !wybrane.has(m.id)).map((m) => m.id))
    const czesc = pole.uloz(pomin)
    wynik.moduly.push(...(czesc.moduly || []))
    wynik.wolne.push(...(czesc.wolne || []))
    wynik.szyny.push(...(czesc.szyny || []))
    wynik.wsporniki.push(...(czesc.wsporniki || []))
    wynik.nogi.push(...(czesc.nogi || []))
    wynik.belki.push(...(czesc.belki || []))
    wynik.pola.push({
      id: pole.id, nazwa: pole.nazwa, pojemnosc: pole.miejsca.length, liczba: (czesc.moduly || []).length,
      uzyskNaKwp: pole.uzyskNaKwp, nachylenie: pole.nachylenie, azymut: pole.azymut, polac: pole.polac || null,
    })
  }

  const grupy = new Map()
  wynik.moduly.forEach((m, i) => {
    const g = kluczGrupy(m)
    if (!grupy.has(g.klucz)) grupy.set(g.klucz, { ...g, indeksy: [] })
    grupy.get(g.klucz).indeksy.push(i)
  })
  wynik.grupy = [...grupy.values()]
  wynik.tryb = tryb
  wynik.docelowa = docelowa
  return wynik
}

/**
 * Przeszkody dla promieni: dach i ściany budynku, kominy i inne przeszkody stojące, lukarny, drzewa, rzędy modułów pochylonych
 * (dach płaski, grunt; moduł nie cieniuje sam siebie: znacznik = indeks modułu), otoczenie z NMPT.
 */
export const przeszkodyProjektu = ({ bryla, projekt, uklad, siatka = null }) => {
  const tri = []
  const znaczniki = []
  const dodaj = (t, znak = -1) => {
    tri.push(...t)
    for (let i = 0; i < t.length / 9; i += 1) znaczniki.push(znak)
  }
  // Połać przycięta do części widocznych (bryla.js) cieniuje tylko nimi.
  for (const p of bryla.polacie || []) for (const w of p.czesci || [p.wierzcholki]) dodaj(trojkatyWielokata3d(w))
  for (const s of bryla.sciany || []) dodaj(trojkatyWielokata3d(s.wierzcholki))
  for (const e of projekt.elementy || []) {
    if (e.typ === 'lukarna' && e.pozycja) {
      const l = brylaLukarny(bryla, e)
      if (l) [...l.sciany, ...l.dachy].forEach((w) => dodaj(trojkatyWielokata3d(w)))
      continue
    }
    if (!PRZESZKODY_STOJACE.includes(e.typ) || !e.pozycja) continue
    const k = brylaStojacej(bryla, e)
    if (k.gora - k.yDachu < 0.05) continue
    // Antena cieniuje masztem, nie podstawą.
    const wymiary = e.typ === 'antena' ? [0.06, k.wymiary[1], 0.06] : k.wymiary
    dodaj(trojkatyPudelka(k.srodek, wymiary, k.obrot))
  }
  uklad.moduly.forEach((m, i) => {
    // Moduły na połaciach skośnych leżą płasko na dachu i nie cieniują się nawzajem.
    if (m.n[1] > 0.995 || !m.pochylony) return
    const u = m.u; const v = m.v
    const hu = m.szer / 2; const hv = m.wys / 2
    const c = m.srodek
    const r = (a, b) => [c[0] + u[0] * a + v[0] * b, c[1] + u[1] * a + v[1] * b, c[2] + u[2] * a + v[2] * b]
    dodaj(trojkatyWielokata3d([r(-hu, -hv), r(hu, -hv), r(hu, hv), r(-hu, hv)]), i)
  })
  const kule = []
  const walce = []
  for (const e of projekt.elementy || []) {
    const t = typElementu(e.typ)
    if (!t || !e.typ.startsWith('drzewo')) continue
    const d = { ...t.domyslne, ...(e.parametry || {}) }
    const [x, y0 = 0, z] = e.pozycja
    if (e.typ === 'drzewo_iglaste') {
      // Stożek przybliżony dwiema kulami i pniem.
      kule.push({ c: [x, y0 + d.wysokosc * 0.42, z], r: d.korona * 0.5 }, { c: [x, y0 + d.wysokosc * 0.72, z], r: d.korona * 0.3 })
    } else {
      kule.push({ c: [x, y0 + d.wysokosc - d.korona * 0.5, z], r: d.korona * 0.5 })
    }
    walce.push({ podstawa: [x, y0, z], wysokosc: d.wysokosc * 0.5, r: 0.2 })
  }
  return zbudujPrzeszkody({ trojkaty: tri, znaczniki, kule, walce, siatka })
}

/** Przelicza azymut siatki (scena) na prawdziwy: prawdziwy = siatki + zbieżność. */
const azPrawdziwy = (az, zbieznosc) => (((az + zbieznosc) % 360) + 360) % 360

/**
 * Analiza roczna: uzysk grup i strata z cienia każdego modułu. Liczona porcjami, żeby nie blokować strony.
 * @returns Promise<{ kwp, rok, miesiace[12], strata (ułamek energii), grupy: [...], stratyModulow: Float32Array }>
 */
export const analizaRoczna = async ({ uklad, przeszkody, lat, lon, zbieznosc = 0, mocModuluW, naPostep, przerwij }) => {
  const probki = probkiRoku({ lat, lon, zbieznosc, krokMin: 30 })
  const n = uklad.moduly.length
  const cienModulow = Array.from({ length: n }, () => new Float32Array(probki.length))
  const porcja = 6
  for (let i = 0; i < n; i += porcja) {
    if (przerwij && przerwij()) return null
    for (let k = i; k < Math.min(n, i + porcja); k += 1) {
      const m = uklad.moduly[k]
      for (let s = 0; s < probki.length; s += 1) cienModulow[k][s] = ulamekCienia(przeszkody, m.probki, probki[s].wektor, k)
    }
    if (naPostep) naPostep(Math.min(1, (i + porcja) / Math.max(1, n)))
    // Oddaj wątek przeglądarce między porcjami.
    // eslint-disable-next-line no-await-in-loop
    await new Promise((r) => setTimeout(r, 0))
  }
  const stratyModulow = new Float32Array(n)
  const miesiace = new Array(12).fill(0)
  let rok = 0
  let rokBez = 0
  const grupy = uklad.grupy.map((g) => {
    const plaszczyzna = { nachylenie: g.nachylenie, azymut: azPrawdziwy(g.azymut, zbieznosc) }
    const u = uzyskMiesieczny({ lat, lon, ...plaszczyzna })
    const kwp = (g.indeksy.length * mocModuluW) / 1000
    let energiaGrupy = 0
    for (const k of g.indeksy) {
      const strata = strataCienia({ lat, lon, plaszczyzna, probki, cien: cienModulow[k] })
      stratyModulow[k] = strata
      energiaGrupy += (u.rok * mocModuluW) / 1000 * (1 - strata)
    }
    const bez = u.rok * kwp
    const ulamek = bez > 0 ? energiaGrupy / bez : 1
    u.miesiace.forEach((m, i) => { miesiace[i] += m * kwp * ulamek })
    rok += energiaGrupy
    rokBez += bez
    return { ...g, plaszczyzna, kwp, uzyskNaKwp: u.rok, uzyskBezCienia: bez, uzysk: energiaGrupy, strata: bez > 0 ? 1 - energiaGrupy / bez : 0 }
  })
  const kwp = (n * mocModuluW) / 1000
  return { kwp, rok, rokBez, strata: rokBez > 0 ? 1 - rok / rokBez : 0, miesiace, grupy, stratyModulow }
}

/** Przebieg mocy w ciągu dnia z cieniem na każdym module (moc przy bezchmurnym niebie). */
export const przebiegDniaProjektu = ({ uklad, przeszkody, lat, lon, zbieznosc = 0, data, mocModuluW }) => {
  const plaszczyzny = uklad.grupy.map((g) => ({
    nachylenie: g.nachylenie, azymut: azPrawdziwy(g.azymut, zbieznosc), mocKwp: (g.indeksy.length * mocModuluW) / 1000,
  }))
  const pamiec = new Map()
  const cien = (wektor, gi) => {
    const klucz = `${gi}:${wektor.map((x) => x.toFixed(3)).join(',')}`
    if (pamiec.has(klucz)) return pamiec.get(klucz)
    const g = uklad.grupy[gi]
    let suma = 0
    for (const k of g.indeksy) suma += ulamekCienia(przeszkody, uklad.moduly[k].probki, wektor, k)
    const f = g.indeksy.length ? suma / g.indeksy.length : 0
    pamiec.set(klucz, f)
    return f
  }
  return przebiegDnia({ lat, lon, rok: data.rok, miesiac: data.miesiac, dzien: data.dzien, zbieznosc, plaszczyzny, cien, krokMin: 10 })
}

/** Cień każdego modułu w danej chwili (do podświetlenia i mocy chwilowej). */
export const cienieTeraz = ({ uklad, przeszkody, wektor }) => uklad.moduly.map((m, k) => (wektor[1] <= 0 ? 1 : ulamekCienia(przeszkody, m.probki, wektor, k)))

/** Kolor modułu na mapie nasłonecznienia: zielony przy pełnym uzysku, żółty, czerwony przy dużej stracie. */
export const kolorStraty = (strata) => {
  const s = Math.max(0, Math.min(1, strata))
  if (s < 0.03) return '#22c55e'
  if (s < 0.08) return '#84cc16'
  if (s < 0.15) return '#eab308'
  if (s < 0.25) return '#f97316'
  return '#ef4444'
}
